import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/*
 * First-run guided-tour controller (Foundation PRD v0.3 — owner onboarding /
 * first-run experience; Paulo's 2026-06-23 request). Pairs with the presentational
 * <GuidedTour> / <OwnerGuidedTour> in components/GuidedTour.jsx.
 *
 * Responsibilities (all client-side, no new deps):
 *   - Auto-run the tour ONCE per browser for owner-side users, persisting a
 *     "seen" flag in localStorage. Never auto-runs again after Done/Skip/Esc.
 *   - Expose a replay trigger (startOwnerTour) that works from anywhere — the
 *     "Take the tour" affordance on Settings dispatches it. Replay works even
 *     after the seen-flag is set.
 *   - Gate auto-run to owner-side roles only (owner_contact / accountant /
 *     stakeholder — mirrors App.OWNER_ROLES). Staff never get the auto-pop.
 *     Replay is unconditional so it also works in an act-as / demo session
 *     (useful for Marieke recording the walkthrough video).
 */

const SEEN_KEY_PREFIX = 'aop_owner_tour_seen';
const START_EVENT = 'aop:start-tour';

// Owner-side roles that get the first-run tour (mirrors App.js OWNER_ROLES).
const OWNER_TOUR_ROLES = new Set(['owner_contact', 'accountant', 'stakeholder']);

// Per-owner identity for the seen-flag. The flag is scoped to the *owner*, not
// the browser, so each distinct owner gets their own first-run — including each
// owner a staff member impersonates (impersonation re-renders /auth/me as the
// target owner, so `user` here is the impersonated owner). A real owner on
// their own browser still only sees it once. Prefer a stable id, fall back to
// email; returns null when we can't identify anyone (→ no auto-run).
function tourIdentity(user) {
  if (!user) return null;
  if (user.user_id != null) return `u:${user.user_id}`;
  if (user.id != null) return `u:${user.id}`;
  if (user.email) return `e:${String(user.email).toLowerCase()}`;
  return null;
}

function seenKey(identity) {
  return `${SEEN_KEY_PREFIX}:${identity}`;
}

function hasSeenTour(identity) {
  if (!identity) return true; // can't identify → don't auto-pop
  try {
    return window.localStorage.getItem(seenKey(identity)) === '1';
  } catch {
    // localStorage blocked (private mode / locked-down browser) → behave as
    // "already seen" so we never nag a user we can't remember dismissing it.
    return true;
  }
}

function markSeen(identity) {
  if (!identity) return;
  try {
    window.localStorage.setItem(seenKey(identity), '1');
  } catch {
    /* storage unavailable — nothing to persist, tour just won't be remembered */
  }
}

// Make sure the owner-nav anchor targets exist before the tour opens: the
// data-tour attributes live on the Home sidebar, so normalise to Home (#/)
// first. First-run logins already land here; this also covers deep-links and
// replays fired from another page.
function ensureHome() {
  const hash = window.location.hash;
  if (hash && hash !== '#/' && hash !== '#') {
    window.location.hash = '#/';
  }
}

// Replay entry point — call from anywhere (e.g. the Settings "Take the tour"
// button) to (re)start the tour. Decoupled via a window event so callers don't
// need to thread props/context through the tree.
export function startOwnerTour() {
  window.dispatchEvent(new CustomEvent(START_EVENT));
}

export default function useFirstRunTour() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isOwner = !!user && OWNER_TOUR_ROLES.has(user.role);
  const identity = tourIdentity(user);

  // Auto-run once per owner identity (re-evaluates when the owner changes, e.g.
  // a staff member switching which owner they're impersonating).
  useEffect(() => {
    if (!isOwner || hasSeenTour(identity)) return undefined;
    // Brief delay so the Home shell has painted before we measure/anchor.
    const t = setTimeout(() => {
      ensureHome();
      setOpen(true);
    }, 600);
    return () => clearTimeout(t);
  }, [isOwner, identity]);

  // Replay trigger (works regardless of role / seen-flag).
  useEffect(() => {
    const onStart = () => {
      ensureHome();
      setOpen(true);
    };
    window.addEventListener(START_EVENT, onStart);
    return () => window.removeEventListener(START_EVENT, onStart);
  }, []);

  // Done / Skip / Esc / backdrop-close all land here: remember it (for the
  // current owner) and close.
  const close = useCallback(() => {
    markSeen(identity);
    setOpen(false);
  }, [identity]);

  return { open, close, enabled: isOwner };
}
